import { chain } from "lodash";
import { Anskaffningsformer, Arbetsordertyper } from "~app/common/models/constants";
import { ValidationMessageType } from "~app/common/validation/validation-message-row/validation-message-row.component";
import { ITplArbetsoderHandelssortimentDetails, ITplArbetsorder, ITplHandelssortiment } from "../../models/dtos";
import { ITplArbetsorderDestineringDto, ITplDestineringDto } from "../tpl-karta/tpl-karta-models";

// När ITplArbetsorderDestineringDto inte längre behöver finnas i stateService kan extenden tas bort
export interface ITplOmdestineraViewModel extends ITplArbetsorder, ITplOmdestineraWarning, ITplArbetsorderDestineringDto {
    isDestinerad: boolean;
    isSelected: boolean;
    allDestinerad: boolean;
    isExternArbetsorder: boolean;
    isTerminalImport: boolean;
    handelssortimentInfo: ITplArbetsoderHandelssortimentDetails;
}

export interface ITplOmdestineraWarning {
    hasWarning: boolean;
    warningVisible: boolean;
    warnings: ITplWarningText[];
}

export interface ITplWarningText {
    brief: string;
    long: string;
    type: ValidationMessageType;
}

export interface ITplValidationMessage {
    text: string;
    type: ValidationMessageType;
}

export class TplOmdestineraViewModel implements ITplOmdestineraViewModel {
    hasWarning = false;
    warningVisible = false;
    warnings = [];
    isSelected = false;
    hover = false;
    handelssortimentInfo: ITplArbetsoderHandelssortimentDetails;
    affarsform: string;
    hasVaglager: boolean;
    isDestinerad: boolean;
    isDelad: boolean;
    allDestinerad: boolean;
    destineringar: ITplDestineringDto[];
    produktionsomradeId: number;

    constructor(readonly model: ITplArbetsorder) {
        this.hasVaglager = model.handelssortiment.some(x => x.vagVolym > 0);
        this.isDestinerad = model.handelssortiment.some(x1 => x1.destineringar?.length > 0);
        this.allDestinerad = model.handelssortiment.every(x => x.destineringar?.length > 0);
        this.isDelad = this.isDestinerad && !this.allDestinerad;

        this.destineringar = chain(model.handelssortiment)
            .map(x => x.destineringar)
            .flatten()
            .uniqBy(x => x.mottagningsplatsId)
            .map(x => ({
                id: x.id,
                mottagningsplatsId: x.mottagningsplatsId,
                namn: null,
                easting: x.easting,
                northing: x.northing
            }))
            .value();
    }

    get arbetsorderId() { return this.model.id; }
    get id() { return this.model.id; }
    get beteckning() { return this.model.beteckning; }
    get northing() { return this.model.northing; }
    get easting() { return this.model.northing; }
    get produktionsstatus() { return this.model.produktionsstatus; }
    get maskinlagId() { return this.model.maskinlagId; }
    get transportforetagId() { return this.model.transportforetagId; }
    get color() { return this.model.color; }
    get anskaffningsform() { return this.model.anskaffningsform; }
    get isTerminalImport(): boolean {
        return (this.anskaffningsform === Anskaffningsformer.EgenTerminal ||
            this.anskaffningsform === Anskaffningsformer.Import) &&
            this.model.typ === Arbetsordertyper.IngenProduktion;
    }

    get isExternArbetsorder(): boolean {
        return ((this.anskaffningsform === Anskaffningsformer.EgenTerminal && !this.model.viol3) ||
            (this.anskaffningsform === Anskaffningsformer.Import && !this.model.viol3) ||
            this.anskaffningsform === Anskaffningsformer.CentralaKop) &&
            this.model.typ === Arbetsordertyper.IngenProduktion;
    }

    get transportPaborjadDatum() { return this.model.transportPaborjadDatum; }
    get transportAvslutadDatum() { return this.model.transportAvslutadDatum; }
    get tillganglighetVag() { return this.model.tillganglighetVag; }
    get namn() { return this.model.namn; }
    get periodStart() { return this.model.periodStart; }
    get periodSlut() { return this.model.periodSlut; }
    get skordningPlaneratStartDatum() { return this.model.skordningPlaneratStartDatum; }
    get skordningPlaneratSlutDatum() { return this.model.skordningPlaneratSlutDatum; }
    get skotningPlaneratStartDatum() { return this.model.skotningPlaneratStartDatum; }
    get skotningPlaneratSlutDatum() { return this.model.skotningPlaneratSlutDatum; }
    get skotningAvslutadDatum() { return this.model.skotningAvslutadDatum; }
    get grotSkordningAvslutadDatum() { return this.model.grotSkordningAvslutadDatum; }

    get handelssortiment(): ITplHandelssortiment[] { return this.model.handelssortiment; }
    get nasFranVag() { return this.model.nasFranVag; }
    get harAktivVirkesorderVals() { return this.model.harAktivVirkesorderVals; }
    get viol3(): boolean {
        return this.model.viol3;
    }
    get typ(): string {
        return this.model.typ;
    }
}

export interface ITplArbetsordrarDestineringDto {
    arbetsorderId: number;
    isDestinerad: boolean;
    hasVaglager: boolean;
    lagerplatser: ITplLagerplatsDto[];
}

export interface ITplLagerplatsDto {
    id: number;
    easting?: number;
    northing?: number;
    namn: string;
    mottagningsplatsId?: number;
}
